import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole } from '../types';
import { authService } from '../core/authService';
import { getAccessToken, setTokens, clearTokens } from '../core/apiClient';
import apiClient from '../core/apiClient';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<User>;
  signup: (userData: any) => Promise<User>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      const userData = authService.getUserFromToken(token);
      
      if (userData) {
        // Only update if it's different to avoid re-render loops
        setUser(prevUser => {
          if (JSON.stringify(prevUser) !== JSON.stringify(userData)) {
            return userData;
          }
          return prevUser;
        });
      } else {
        clearTokens();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await apiClient.post('/api/auth/login', { email, password });
      const { accessToken, refreshToken } = response.data.data;
      
      setTokens(accessToken, refreshToken);
      const userData = authService.getUserFromToken(accessToken);
      if (!userData) throw new Error('Invalid token received');
      
      setUser(userData);
      return userData;
    } catch (error: any) {
      const message = error.message || 'Invalid email or password';
      throw new Error(message);
    }
  };

  const signup = async (userData: any): Promise<User> => {
    try {
      const response = await apiClient.post('/api/auth/signup', userData);
      const { accessToken, refreshToken } = response.data.data;
      
      setTokens(accessToken, refreshToken);
      const user = authService.getUserFromToken(accessToken);
      if (!user) throw new Error('Invalid token received');
      
      setUser(user);
      return user;
    } catch (error: any) {
      const message = error.message || 'Signup failed';
      throw new Error(message);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Only call logout if we have a real JWT (check for dots in token)
      const token = getAccessToken();
      if (token && token.includes('.')) {
        await apiClient.post('/api/auth/logout');
      }
    } catch (e) {
      console.error('Logout API failed', e);
    } finally {
      clearTokens();
      setUser(null);
      window.location.href = '/home';
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
